if (!customElements.get('nb-announcement-bar')) {
  class NonbakAnnouncementBar extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.viewport = this.querySelector('[data-announcement-viewport]');
      this.abortController = new AbortController();
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.activeIndex = 0;

      if (this.dataset.mode === 'marquee') this.setupMarquee();
      else this.setupSlideshow();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      window.clearInterval(this.autoplayTimer);
      window.cancelAnimationFrame(this.resizeFrame);
      window.cancelAnimationFrame(this.scrollFrame);
      delete this.dataset.ready;
    }

    setupMarquee() {
      this.track = this.querySelector('[data-announcement-marquee-track]');
      this.cycle = this.querySelector('[data-announcement-marquee-cycle]');
      if (!this.viewport || !this.track || !this.cycle) return;

      const rebuild = () => {
        this.track.classList.remove('is-ready');
        this.track.querySelectorAll('[data-generated-cycle]').forEach((node) => node.remove());
        this.cycle.querySelectorAll('[data-generated-item]').forEach((node) => node.remove());
        if (this.motionQuery.matches) return;

        const originalItems = [...this.cycle.children];
        if (!originalItems.length) return;

        let repetitions = 0;
        while (this.cycle.scrollWidth < this.viewport.clientWidth * 1.15 && repetitions < 24) {
          originalItems.forEach((item) => {
            const clone = this.prepareClone(item);
            clone.dataset.generatedItem = '';
            this.cycle.appendChild(clone);
          });
          repetitions += 1;
        }

        const duplicateCycle = this.prepareClone(this.cycle);
        duplicateCycle.removeAttribute('data-announcement-marquee-cycle');
        duplicateCycle.dataset.generatedCycle = '';
        this.track.appendChild(duplicateCycle);

        const distance = this.cycle.getBoundingClientRect().width;
        const speed = Math.max(10, Number(this.dataset.marqueeSpeed) || 35);
        this.track.style.setProperty('--nb-announcement-distance', `${distance}px`);
        this.track.style.setProperty('--nb-announcement-duration', `${distance / speed}s`);
        window.requestAnimationFrame(() => this.track.classList.add('is-ready'));
      };

      this.resizeObserver = new ResizeObserver(() => {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = window.requestAnimationFrame(rebuild);
      });
      this.resizeObserver.observe(this.viewport);
      rebuild();
    }

    prepareClone(node) {
      const clone = node.cloneNode(true);
      [clone, ...clone.querySelectorAll('*')].forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
      });
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    setupSlideshow() {
      this.slides = [...this.querySelectorAll('[data-announcement-slide]')];
      this.previousButton = this.querySelector('[data-announcement-previous]');
      this.nextButton = this.querySelector('[data-announcement-next]');
      if (!this.viewport || this.slides.length < 2) return;

      const { signal } = this.abortController;
      const move = (direction) => {
        this.goTo(this.activeIndex + direction);
        this.restartAutoplay();
      };

      this.previousButton?.addEventListener('click', () => move(-1), { signal });
      this.nextButton?.addEventListener('click', () => move(1), { signal });
      this.viewport.addEventListener('scroll', () => {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => this.updateActiveSlide());
      }, { passive: true, signal });
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      }, { signal });
      this.addEventListener('pointerenter', () => this.stopAutoplay(), { signal });
      this.addEventListener('pointerleave', () => this.startAutoplay(), { signal });
      this.addEventListener('focusin', () => this.stopAutoplay(), { signal });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.startAutoplay();
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const slide = event.target.closest('[data-announcement-slide]');
        if (!slide) return;
        this.stopAutoplay();
        this.goTo(this.slides.indexOf(slide), false);
      }, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      }, { signal });

      this.resizeObserver = new ResizeObserver(() => this.goTo(this.activeIndex, false));
      this.resizeObserver.observe(this.viewport);
      this.updateSlideState();
      this.startAutoplay();
    }

    goTo(index, smooth = true) {
      if (!this.viewport || !this.slides?.length) return;
      this.activeIndex = (index + this.slides.length) % this.slides.length;
      this.viewport.scrollTo({
        left: this.activeIndex * this.viewport.clientWidth,
        behavior: smooth && !this.motionQuery.matches ? 'smooth' : 'auto'
      });
      this.updateSlideState();
    }

    updateActiveSlide() {
      if (!this.viewport?.clientWidth) return;
      this.activeIndex = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
      this.updateSlideState();
    }

    updateSlideState() {
      this.slides?.forEach((slide, index) => {
        if (index === this.activeIndex) slide.setAttribute('aria-current', 'true');
        else slide.removeAttribute('aria-current');
      });
    }

    startAutoplay() {
      if (
        this.dataset.autoplay !== 'true'
        || this.motionQuery.matches
        || document.hidden
        || this.slides?.length < 2
      ) return;

      this.stopAutoplay();
      const interval = Math.max(3, Number(this.dataset.autoplayInterval) || 5) * 1000;
      this.autoplayTimer = window.setInterval(() => this.goTo(this.activeIndex + 1), interval);
    }

    stopAutoplay() {
      window.clearInterval(this.autoplayTimer);
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  customElements.define('nb-announcement-bar', NonbakAnnouncementBar);
}
